import './c100-fonts'
import {
  Box, Container, InputAdornment, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TableSortLabel, TextField, Typography,
  useTheme
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { useEffect, useMemo, useState } from 'react'
import PageProps from '../_types/page-props.interface'
import useDocumentTitle from '../hooks/useDocumentTitle'
import { useError } from '../context/error'
import { StyleHelper } from '../components/style-helper'
import LoadingTable from '../components/loading/table.loading'
import { Carnivore100Service } from '../services/carnivore100.service'
import { C100Payload, C100Row, C100Timeframe } from '../_types/carnivore100.type'
import {
  filterRows, sortRows, uniqueMarkets, uniqueSectors, SortKey, SortDir
} from './carnivore100/carnivore100.util'
import TimeframeSelector from '../components/carnivore100/timeframe-selector'
import C100BannerView from '../components/carnivore100/banner'
import FilterChips from '../components/carnivore100/filter-chips'
import SparkCell from '../components/carnivore100/spark-cell'
import RankBadge from '../components/carnivore100/rank-badge'
import { c100Tokens, sectorColor, scrollbarSx } from '../components/carnivore100/c100-tokens'
import { useTickerDrawer } from '../context/ticker-drawer-context'

const MONO = "'IBM Plex Mono', monospace"

const fmtPct = (n: number | null) =>
  n === null ? '—' : `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`

const COLUMNS: { key: SortKey; label: string; numeric: boolean }[] = [
  { key: 'rank', label: '#', numeric: true },
  { key: 'ticker', label: 'Ticker', numeric: false },
  { key: 'name', label: 'Name', numeric: false },
  { key: 'sector', label: 'Sector', numeric: false },
  { key: 'market', label: 'Mkt', numeric: false },
  { key: 'price', label: 'Price', numeric: true },
  { key: 'dayChangePct', label: 'Day', numeric: true },
  { key: 'timeframeChangePct', label: 'Change', numeric: true }
]

const rowFadeKeyframes = `
@keyframes c100RowFadeIn {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}
`

export default function Carnivore100Page(props: PageProps) {
  useDocumentTitle(props.title)
  const { setError } = useError()
  const service = useMemo(() => new Carnivore100Service(setError), [setError])
  const theme = useTheme()
  const t = c100Tokens(theme)
  const { isOpen, setTicker, toggleDrawer } = useTickerDrawer()

  const openTicker = (ticker: string) => {
    setTicker(ticker)
    if (!isOpen) toggleDrawer()
  }

  const [timeframe, setTimeframe] = useState<C100Timeframe>('1D')
  const [payload, setPayload] = useState<C100Payload | null>(null)
  const [loading, setLoading] = useState(true)
  const [errored, setErrored] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  const [search, setSearch] = useState('')
  const [sector, setSector] = useState('All')
  const [market, setMarket] = useState('All')
  const [sortKey, setSortKey] = useState<SortKey>('rank')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  useEffect(() => {
    let active = true
    ;(async () => {
      setLoading(true)
      setErrored(false)
      const res = await service.getTable(timeframe)
      if (!active) return
      if (res && !(res as any).errMsg) setPayload(res as C100Payload)
      else setErrored(true)
      setLoading(false)
    })()
    return () => {
      active = false
    }
  }, [service, timeframe, reloadKey])

  const rows: C100Row[] = payload?.rows ?? []
  const sectors = useMemo(() => uniqueSectors(rows), [rows])
  const markets = useMemo(() => uniqueMarkets(rows), [rows])
  const visible = useMemo(
    () => sortRows(filterRows(rows, { search, sector, market }), sortKey, sortDir),
    [rows, search, sector, market, sortKey, sortDir]
  )

  const onSort = (key: SortKey) => {
    if (key === sortKey) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(key)
      const ascFirst = key === 'rank' || key === 'ticker' || key === 'name' || key === 'sector' || key === 'market'
      setSortDir(ascFirst ? 'asc' : 'desc')
    }
  }

  return (
    <Box sx={{ bgcolor: t.page, minHeight: '100vh' }}>
      {/* inject keyframes */}
      <style>{rowFadeKeyframes}</style>

      <Container sx={{ display: 'flex', flexDirection: 'column', pb: 4, pt: 3 }}>
        {/* Page header */}
        <Box sx={{ mb: 2.5 }}>
          <Typography
            sx={{
              fontFamily: MONO,
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: '0.08em',
              color: t.text,
              textTransform: 'uppercase',
              lineHeight: 1.1
            }}
          >
            CARNIVORE 100
          </Typography>
          <Typography
            sx={{
              fontFamily: MONO,
              fontSize: 10,
              letterSpacing: '0.12em',
              color: t.muted,
              textTransform: 'uppercase',
              mt: 0.5
            }}
          >
            {`WATCHLIST${payload?.version ? ` · v${payload.version}` : ''}`}
          </Typography>
        </Box>

        <TimeframeSelector value={timeframe} onChange={setTimeframe} />

        {loading && <LoadingTable rowCount={8} columnCount={8} />}

        {!loading && errored && (
          <Box
            sx={{
              bgcolor: t.card,
              border: `1px solid ${t.hairline}`,
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              mt: 2
            }}
          >
            <Typography sx={{ color: t.text, fontFamily: MONO, fontSize: 13 }}>
              Couldn't load the Carnivore 100.
            </Typography>
            <Typography sx={{ color: t.muted, fontFamily: MONO, fontSize: 11, mt: 0.75 }}>
              The market data service didn't respond in time.
            </Typography>
            <Box
              component="button"
              onClick={() => setReloadKey(k => k + 1)}
              sx={{
                mt: 2.5,
                cursor: 'pointer',
                fontFamily: MONO,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: t.accent,
                bgcolor: t.faintAccentBg,
                border: `1px solid ${t.accent}`,
                borderRadius: 999,
                px: 2.5,
                py: 0.75,
                transition: 'background-color .15s',
                '&:hover': { bgcolor: t.accent, color: '#fff' }
              }}
            >
              Retry
            </Box>
          </Box>
        )}

        {!loading && !errored && payload && (
          <>
            <C100BannerView banner={payload.banner} timeframe={timeframe} />

            {/* Control bar */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1.5,
                alignItems: { xs: 'stretch', sm: 'flex-start' },
                mb: 1.5
              }}
            >
              {/* Search */}
              <TextField
                size="small"
                placeholder="Search ticker or name…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 16, color: t.muted }} />
                    </InputAdornment>
                  )
                }}
                sx={{
                  width: { xs: '100%', sm: 280 },
                  flexShrink: 0,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: t.elevated,
                    borderRadius: 999,
                    fontFamily: MONO,
                    fontSize: 12,
                    '& fieldset': { borderColor: t.hairline },
                    '&:hover fieldset': { borderColor: t.muted },
                    '&.Mui-focused fieldset': { borderColor: t.accent, borderWidth: 1 }
                  },
                  '& input': {
                    color: t.text,
                    '&::placeholder': { color: t.muted, opacity: 1 }
                  }
                }}
              />

              {/* Filters */}
              <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                <FilterChips label="Sector" options={sectors} value={sector} onChange={setSector} withDots />
                <FilterChips label="Market" options={markets} value={market} onChange={setMarket} />
              </Box>
            </Box>

            {rows.length === 0 ? (
              <Box
                sx={{
                  bgcolor: t.card,
                  border: `1px solid ${t.hairline}`,
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  mt: 2
                }}
              >
                <Typography sx={{ color: t.muted, fontFamily: MONO, fontSize: 13 }}>
                  The Carnivore 100 hasn't been published yet. Check back soon.
                </Typography>
              </Box>
            ) : (
              <TableContainer
                component={Box}
                sx={{
                  bgcolor: t.card,
                  border: `1px solid ${t.hairline}`,
                  borderRadius: 2,
                  maxHeight: 800,
                  overflow: 'auto',
                  mt: 1,
                  ...scrollbarSx(t)
                }}
              >
                <Table stickyHeader aria-label="carnivore 100 table" size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        '& th': {
                          bgcolor: t.card,
                          borderBottom: `1px solid ${t.hairline}`,
                          position: 'sticky',
                          top: 0,
                          zIndex: 20
                        }
                      }}
                    >
                      {COLUMNS.map(col => (
                        <TableCell
                          key={col.key}
                          sortDirection={sortKey === col.key ? sortDir : false}
                          sx={{ py: 1.25 }}
                        >
                          <TableSortLabel
                            active={sortKey === col.key}
                            direction={sortKey === col.key ? sortDir : 'asc'}
                            onClick={() => onSort(col.key)}
                            sx={{
                              fontFamily: MONO,
                              fontSize: 10.5,
                              letterSpacing: '0.08em',
                              textTransform: 'uppercase',
                              color: sortKey === col.key ? `${t.accent} !important` : `${t.muted} !important`,
                              '& .MuiTableSortLabel-icon': {
                                color: `${t.accent} !important`,
                                opacity: sortKey === col.key ? 1 : 0
                              },
                              '&:hover': { color: `${t.text} !important` }
                            }}
                          >
                            {col.label}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                      <TableCell
                        sx={{
                          fontFamily: MONO,
                          fontSize: 10.5,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          color: t.muted,
                          py: 1.25
                        }}
                      >
                        Trend
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: MONO,
                          fontSize: 10.5,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          color: t.muted,
                          py: 1.25
                        }}
                      >
                        Spark
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visible.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={10}
                          sx={{
                            textAlign: 'center',
                            py: 4,
                            color: t.muted,
                            fontFamily: MONO,
                            fontSize: 12,
                            border: 'none'
                          }}
                        >
                          No tickers match your search or filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                      visible.map((r, index) => {
                        const dayColor = r.dayChangePct === null ? t.muted : r.dayChangePct >= 0 ? t.gain : t.loss
                        const chgColor = r.timeframeChangePct === null ? t.muted : r.timeframeChangePct >= 0 ? t.gain : t.loss
                        const delay = Math.min(index * 12, 300)
                        return (
                          <TableRow
                            key={r.ticker}
                            onClick={() => openTicker(r.ticker)}
                            sx={{
                              height: 56,
                              cursor: 'pointer',
                              borderBottom: `1px solid ${t.hairline}`,
                              '&:last-child td': { borderBottom: 'none' },
                              transition: 'background-color .15s, box-shadow .15s',
                              '&:hover': {
                                bgcolor: t.faintAccentBg,
                                boxShadow: `inset 3px 0 0 ${t.accent}`
                              },
                              animation: `c100RowFadeIn 0.3s ease both`,
                              animationDelay: `${delay}ms`
                            }}
                          >
                            {/* Rank + movement badge */}
                            <TableCell sx={{ border: 'none', py: 1, whiteSpace: 'nowrap' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Typography
                                  sx={{
                                    fontFamily: MONO,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: t.text,
                                    fontVariantNumeric: 'tabular-nums',
                                    minWidth: 22
                                  }}
                                >
                                  {r.rank}
                                </Typography>
                                <RankBadge rankDelta={r.rankDelta} isNew={r.isNew} />
                              </Box>
                            </TableCell>

                            {/* Ticker */}
                            <TableCell sx={{ border: 'none', py: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: sectorColor(r.sector),
                                    flexShrink: 0
                                  }}
                                />
                                <Typography
                                  sx={{
                                    fontFamily: MONO,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: t.text,
                                    letterSpacing: '0.04em'
                                  }}
                                >
                                  {r.ticker}
                                </Typography>
                              </Box>
                            </TableCell>

                            {/* Name */}
                            <TableCell sx={{ border: 'none', py: 1, maxWidth: 180 }}>
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  color: t.text,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {r.name}
                              </Typography>
                            </TableCell>

                            {/* Sector */}
                            <TableCell sx={{ border: 'none', py: 1, maxWidth: 140 }}>
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  fontWeight: 500,
                                  color: sectorColor(r.sector),
                                  lineHeight: 1.2
                                }}
                              >
                                {r.sector || '—'}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 10,
                                  color: t.muted,
                                  mt: 0.1,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: 130
                                }}
                              >
                                {r.industry}
                              </Typography>
                            </TableCell>

                            {/* Market pill */}
                            <TableCell sx={{ border: 'none', py: 1 }}>
                              <Box
                                sx={{
                                  display: 'inline-block',
                                  fontFamily: MONO,
                                  fontSize: 10,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.04em',
                                  color: t.muted,
                                  border: `1px solid ${t.hairline}`,
                                  borderRadius: '6px',
                                  px: '6px',
                                  py: '1px',
                                  lineHeight: 1.6
                                }}
                              >
                                {r.market || '—'}
                              </Box>
                            </TableCell>

                            {/* Price */}
                            <TableCell sx={{ border: 'none', py: 1 }}>
                              <Typography
                                sx={{
                                  fontFamily: MONO,
                                  fontSize: 13,
                                  color: t.text,
                                  fontVariantNumeric: 'tabular-nums'
                                }}
                              >
                                {r.price != null ? `$${r.price.toFixed(2)}` : '—'}
                              </Typography>
                            </TableCell>

                            {/* Day */}
                            <TableCell sx={{ border: 'none', py: 1 }}>
                              <Typography
                                sx={{
                                  fontFamily: MONO,
                                  fontSize: 13,
                                  color: dayColor,
                                  fontVariantNumeric: 'tabular-nums',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.25
                                }}
                              >
                                {r.dayChangePct !== null && (
                                  <span>{r.dayChangePct >= 0 ? '▲' : '▼'}</span>
                                )}
                                {fmtPct(r.dayChangePct)}
                              </Typography>
                            </TableCell>

                            {/* Change */}
                            <TableCell sx={{ border: 'none', py: 1 }}>
                              <Typography
                                sx={{
                                  fontFamily: MONO,
                                  fontSize: 13,
                                  color: chgColor,
                                  fontVariantNumeric: 'tabular-nums',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.25
                                }}
                              >
                                {r.timeframeChangePct !== null && (
                                  <span>{r.timeframeChangePct >= 0 ? '▲' : '▼'}</span>
                                )}
                                {fmtPct(r.timeframeChangePct)}
                              </Typography>
                            </TableCell>

                            {/* Trend glyph */}
                            <TableCell sx={{ border: 'none', py: 1 }}>
                              <Typography
                                sx={{
                                  fontFamily: MONO,
                                  fontSize: 14,
                                  color:
                                    r.trend === 'up'
                                      ? t.gain
                                      : r.trend === 'down'
                                      ? t.loss
                                      : t.flat
                                }}
                              >
                                {r.trend === 'up' ? '↗' : r.trend === 'down' ? '↘' : '–'}
                              </Typography>
                            </TableCell>

                            {/* Sparkline */}
                            <TableCell sx={{ border: 'none', py: 1 }}>
                              <SparkCell values={r.sparkline} />
                            </TableCell>
                          </TableRow>
                        )
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <StyleHelper.VerticalSpace />
            <Stack alignItems="center">
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: 11,
                  letterSpacing: '0.06em',
                  color: t.muted
                }}
              >
                Showing {visible.length} of {rows.length} · v{payload.version}
              </Typography>
            </Stack>
          </>
        )}
      </Container>
    </Box>
  )
}
